import { apiClient } from '../../api/fetchClient';
import { shopifyClient } from '../../shopify/shopify.client';
import type {
  Contract,
  Membership,
  MembershipContractAddressUpdateRequest,
  MembershipContractUpdateCancellationRequest,
  MembershipContractUpdateRequest,
  ReferralLink,
  WalletType,
} from '../models/membership.model';
import {
  getMembershipForProduct,
  getTierForProduct,
  isMembershipMaxMembersSpecified,
  isTierMaxMembersSpecified,
} from '../utils/membership.utlis';

class MembershipService {
  async getManageData(): Promise<Membership[]> {
    return apiClient.get<Membership[]>('/apps/single/v3/fan-subscription/manage');
  }

  async getAddressData(): Promise<string> {
    return apiClient.getText('/apps/single/v3/fan-subscription/address-data');
  }

  async updateMembership(request: MembershipContractUpdateRequest): Promise<void> {
    return apiClient.post<void>('/apps/single/v3/fan-subscription/update', request);
  }

  async cancelMembershipUpdate(
    request: MembershipContractUpdateCancellationRequest
  ): Promise<void> {
    return apiClient.post<void>('/apps/single/v3/fan-subscription/update/cancel', request);
  }

  async updateAddress(request: MembershipContractAddressUpdateRequest): Promise<void> {
    return apiClient.post<void>('/apps/single/v3/fan-subscription/address/update', request);
  }

  async updatePaymentMethod(contract: Contract): Promise<void> {
    return apiClient.post<void>('/apps/single/v3/fan-subscription/payment-method/update', {
      contractId: contract.id,
      sourceContractId: contract.sourceContractId,
      paymentMethodId: contract.sourcePaymentMethodId,
    });
  }

  async getCustomerInfo(): Promise<{ id: string; email: string }> {
    return apiClient.get<{ id: string; email: string }>('/apps/single/v3/customer/data');
  }

  async createContract(
    tierId: string,
    termId: string,
    referralLinkId?: string,
    optInToMarketingEmails?: boolean
  ): Promise<void> {
    return apiClient.post<void>('/apps/single/v3/fan-subscription/create', {
      tierId,
      termId,
      referralLinkId,
      optInToMarketingEmails,
    });
  }

  async getReferralLink(id: string): Promise<ReferralLink> {
    return apiClient.get<ReferralLink>(`/apps/single/v3/fan-subscription/referral-link/${id}`);
  }

  async getWalletPassUrl(contract: Contract, walletType: WalletType): Promise<{ url: string }> {
    return apiClient.get<{ url: string }>(
      `/apps/single/v3/wallet/${walletType}/contract/${contract.id}`
    );
  }


  async reloadMembershipsIfNecessary(
    memberships: Membership[],
    productId: string,
    productVariantId: string
  ): Promise<Membership[]> {
    if (!memberships || memberships.length === 0) {
      return [];
    }

    const membership = getMembershipForProduct(memberships, productId, productVariantId);
    const tier = getTierForProduct(memberships, productId, productVariantId);

    if (isTierMaxMembersSpecified(tier) || isMembershipMaxMembersSpecified(membership)) {
      return await this.getManageData();
    }

    return memberships;
  }

  async getMembershipProducts(memberships: Membership[]): Promise<Membership[]> {
    if (!memberships || memberships.length === 0) {
      return [];
    }

    const productPromises = memberships.flatMap((membership) => {
      const paidTiers = membership.tiers.filter((tier) => !tier.freeTier);

      if (paidTiers.length === 0) {
        return [];
      }

      return paidTiers.map((tier) =>
        shopifyClient
          .getProductPriceData(tier.productHandle)
          .then((product) => {
            const variantMap = new Map();
            product.variants.forEach((variant) => variantMap.set(variant.id, variant));

            tier.terms.forEach((term) => {
              const matchingVariant = term.associatedProductVariants
                ?.filter(
                  (variant) =>
                    variant.singleCreated && variantMap.has(Number(variant.sourceVariantId))
                )
                .map((variant) => variantMap.get(Number(variant.sourceVariantId)))
                .find((variant) => variant !== undefined);

              if (matchingVariant?.price != null) {
                const priceValue = Number(matchingVariant.price);
                term.convertedPrice = isNaN(priceValue) ? term.price : priceValue;
              } else {
                term.convertedPrice = term.price;
              }

              // Update related terms
              if (membership.selectedTerm?.id === term.id) {
                membership.selectedTerm.convertedPrice = term.convertedPrice;
              }
              if (membership.contract?.term?.id === term.id) {
                membership.contract.term.convertedPrice = term.convertedPrice;
              }
              if (membership.contract?.latestPendingChange?.term?.id === term.id) {
                membership.contract.latestPendingChange.term.convertedPrice = term.convertedPrice;
              }
            });

            return product;
          })
          .catch((error) => {
            // Fallback to original price on error
            tier.terms.forEach((term) => {
              term.convertedPrice = term.price;

              if (membership.selectedTerm?.id === term.id) {
                membership.selectedTerm.convertedPrice = term.convertedPrice;
              }
              if (membership.contract?.term?.id === term.id) {
                membership.contract.term.convertedPrice = term.convertedPrice;
              }
              if (membership.contract?.latestPendingChange?.term?.id === term.id) {
                membership.contract.latestPendingChange.term.convertedPrice = term.convertedPrice;
              }
            });

            return error;
          })
      );
    });

    await Promise.all(productPromises);
    return memberships;
  }
}

export const membershipService = new MembershipService();
